// src/components/contacts/LinkedinUrlImport.js
// Sprint 1 E1.4b — Importar contato a partir de URL LinkedIn (Apollo enrich + create).
//
// Variants:
//   - inline (default): input compacto + botão lado a lado, ideal para header de listagem.
//   - standalone: card maior, ideal para modal "+ Novo Contato".
//
// Comportamento:
//   - Valida URL LinkedIn (regex em useLinkedinUrlImport).
//   - Se contato já existe → mostra aviso + onImported(id).
//   - Se criou → onImported(id), depois navigate(/contatos) com focusContatoId no state
//     (a página /contatos já trata location.state.focusContatoId desde Onda 5).

import React, { useState } from 'react';
import { Linkedin, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLinkedinUrlImport } from '../../hooks/useLinkedinUrlImport';

export default function LinkedinUrlImport({
  onImported,
  className = '',
  variant = 'inline',
}) {
  const [url, setUrl] = useState('');
  const { importFromUrl, loading } = useLinkedinUrlImport();
  const navigate = useNavigate();

  const handleImport = async () => {
    const result = await importFromUrl(url);

    if (!result.ok) {
      // Mensagem amigável por tipo de falha (422 URL inválida, 404 não encontrado,
      // demais erros de negócio). result.error já vem legível do hook.
      let msg;
      if (result.error_code === 'invalid_linkedin_url' || result.status === 422) {
        msg = `URL inválida — ${result.error || 'cole um perfil https://www.linkedin.com/in/usuario'}`;
      } else if (result.status === 404) {
        msg = `Não encontrado — ${result.error || 'o Apollo não localizou esse perfil.'}`;
      } else {
        msg = `Falha ao importar — ${result.error || 'erro desconhecido.'}`;
      }
      // eslint-disable-next-line no-alert
      alert(msg);
      return;
    }

    if (result.already_exists) {
      // eslint-disable-next-line no-alert
      alert(`ℹ Contato "${result.contact_name}" já existe — abrindo perfil.`);
      if (onImported && result.contact_id) {
        onImported(result.contact_id);
      } else if (result.contact_id) {
        navigate('/contatos', { state: { focusContatoId: result.contact_id } });
      }
      return;
    }

    // eslint-disable-next-line no-alert
    alert(`✓ ${result.contact_name || 'Contato'} importado${result.empresa_name ? ` de ${result.empresa_name}` : ''}.`);
    setUrl('');
    if (onImported && result.contact_id) {
      onImported(result.contact_id);
    } else if (result.contact_id) {
      navigate('/contatos', { state: { focusContatoId: result.contact_id } });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && url.trim()) handleImport();
  };

  if (variant === 'standalone') {
    return (
      <div className={`p-4 border border-slate-200 rounded-lg bg-slate-50 ${className}`}>
        <h3 className="font-semibold mb-1 text-sm text-slate-800 flex items-center gap-1.5">
          <Linkedin size={14} className="text-blue-700" /> Importar contato via LinkedIn
        </h3>
        <p className="text-xs text-slate-600 mb-3">
          Cole a URL do perfil. O Apollo vai buscar os dados e criar contato + empresa no CRM.
        </p>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="https://linkedin.com/in/usuario"
            disabled={loading}
            className="flex-1 px-2.5 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-blue-400"
          />
          <button
            onClick={handleImport}
            disabled={loading || !url.trim()}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-xs flex items-center gap-1.5 whitespace-nowrap"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Linkedin size={12} />}
            {loading ? 'Buscando…' : 'Buscar via Apollo'}
          </button>
        </div>
      </div>
    );
  }

  // inline
  return (
    <div className={`flex gap-2 items-center ${className}`}>
      <div className="relative flex-1 min-w-[220px]">
        <Linkedin size={12} className="absolute left-2.5 top-2.5 text-slate-400" />
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="https://linkedin.com/in/..."
          disabled={loading}
          className="w-full pl-7 pr-2.5 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-400"
        />
      </div>
      <button
        onClick={handleImport}
        disabled={loading || !url.trim()}
        className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-xs flex items-center gap-1.5 whitespace-nowrap"
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Linkedin size={12} />}
        {loading ? '…' : 'Importar LinkedIn'}
      </button>
    </div>
  );
}
